import type { ReactElement } from 'react';
import styled from 'styled-components';

export interface TabPill {
  /** Unique identifier for the pill. */
  key: string;
  /** Font Awesome icon class (e.g. `far fa-calendar`). Hidden when omitted. */
  icon?: string;
  /** Pill text. Hidden when omitted. */
  label?: string;
  /** Trailing count badge. Hidden when omitted (pass `0` to show a zero). */
  count?: number;
  /** Leading 6px green status dot (e.g. a "Live" pill). Hidden when omitted. */
  dot?: boolean;
  /** Whether the pill is currently selected. */
  active?: boolean;
  /** Invoked when the pill is clicked. */
  onClick?: () => void;
}

interface Props {
  pills: TabPill[];
  /** Slightly more compact variant (smaller paddings and font-size). */
  small?: boolean;
  /** Use a subtler, translucent background for the active pill. */
  darker?: boolean;
  className?: string;
}

const Container = styled.nav<{ $small: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${({ $small }): string => ($small ? '3px' : '4px')};
  background: rgba(17, 27, 51, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  flex-wrap: wrap;
`;

const Pill = styled.button<{ $small: boolean; $darker: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $small }): string => ($small ? '6px' : '8px')};
  padding: ${({ $small }): string => ($small ? '5px 10px' : '7px 13px')};
  border: none;
  border-radius: 7px;
  background: transparent;
  color: var(--white60);
  font-size: ${({ $small }): string => ($small ? '11px' : '12px')};
  font-weight: 600;
  white-space: nowrap;
  transition: all var(--transition);

  &:hover,
  &:focus {
    color: var(--white);
    outline: none;
  }

  &.active {
    background: ${({ $darker }): string => ($darker ? 'rgba(78, 95, 253, 0.16)' : 'rgb(78, 95, 253)')};
    color: rgb(255, 255, 255);
  }

  i {
    font-size: ${({ $small }): string => ($small ? '11px' : '12px')};
  }

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    background: rgb(53, 211, 153);
  }

  .count {
    padding: ${({ $small }): string => ($small ? '1px 5px' : '2px 6px')};
    border-radius: 999px;
    font-size: ${({ $small }): string => ($small ? '10px' : '11px')};
    line-height: 1;
  }

  &.active .count {
  }
`;

/*
 * Reusable pill switcher. Render a set of pills anywhere; each may carry any
 * combination of icon, label and count (missing pieces are simply not shown).
 */
export const TabPills = ({ pills, small = false, darker = false, className }: Props): ReactElement => (
  <Container $small={small} className={className}>
    {pills.map((pill) => (
      <Pill
        key={pill.key}
        type='button'
        $small={small}
        $darker={darker}
        className={pill.active ? 'active' : ''}
        onClick={pill.onClick}
      >
        {pill.dot === true && <span className='dot' />}
        {pill.icon !== undefined && <i className={pill.icon} />}
        {pill.label !== undefined && <span>{pill.label}</span>}
        {pill.count !== undefined && <span className='count'>{pill.count}</span>}
      </Pill>
    ))}
  </Container>
);
